import { useState } from 'react'
import AuthService from 'services/auth'
import cutileiApi from 'services/cutileiApi'
import { logApiError } from 'services/apiError'
import AlertDialog from 'components/AlertDialog'
import NumberInput from 'components/Inputs/NumberInput'
import MoneyInput from 'components/Inputs/MoneyInput'
import Select from 'components/Inputs/Select'
import CheckBox from 'components/CheckBox'
import billServiceValidator from 'validators/billServiceValidator'
import { ReactComponent as ButtonLoading } from 'icons/loading2.svg'
import Logo from 'icons/logo-cutilei-simple.png'
import ReactTooltip from 'react-tooltip'
import { Formik } from 'formik'
import * as FA from 'react-icons/fa'

import {
  Container,
  FormField,
  Image,
  Row,
  CheckRow,
  Input,
  Label,
  InfoText,
  TooltipLabel,
  MoneyTextMask,
  Button
} from './styles'

export default function BillService ({
  bill,
  data: service,
  professionals = [],
  freeItems = [],
  showFreeItemsValue = false,
  enableEditting = true,
  hasEditPermission = true,
  hasDeletePermission = true,
  onToggleFree: handleToggleFree = () => null,
  onEdit: handleEdit,
  onDelete: handleDelete
}) {
  const [editting, setEditting] = useState (false)
  const [showAlertDialog, setShowAlertDialog] = useState (false)
  const [deleting, setDeleting] = useState (false)
  const token = AuthService.getToken ()
  const canEditAmount = AuthService.hasPermission ('bills.items_amount', 'edit')
  const canEditValue = AuthService.hasPermission ('bills.items_value', 'edit')
  const canEditComission = AuthService.hasPermission ('bills.items_comission', 'edit')
  const businessId = AuthService.getBusinessId()
  const freeService = freeItems.find (s => s.id === service.id || service.bundle_id)
  const canBeFree = !!freeService
  const isFree = freeService?.isFree

  const requestConfig = {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  }

  // A comissão do serviço no cadastro do salão é só o default: cada profissional
  // pode ter um percentual próprio para o mesmo serviço, cadastrado em
  // EditProfessional/Services. Trocar o profissional de um item já lançado precisa
  // trazer o percentual dele junto, senão a comanda fecha com a comissão do anterior.
  const fetchProfessionalServices = async (professionalId) => {
    if (!professionalId) return []

    try {
      const { data } = await cutileiApi.get(
        `/businesses/${businessId}/professionals/${professionalId}/services?cutilei_services=false`,
        requestConfig
      )
      return data
    } catch (error) {
      logApiError(error)
      return []
    }
  }

  const handleProfessionalSelected = async (name, option, setFieldValue) => {
    setFieldValue(name, option)

    const professionalServices = await fetchProfessionalServices(option?.value)
    const comission = professionalServices.find(s => s.id === service.id)?.comission_percentage

    // O item da comanda não carrega o percentual padrão do serviço — bills_services só
    // tem custom_comission_percentage. Sem o do profissional, manter o que já estava
    // salvo é melhor do que escrever NaN no campo.
    if (comission !== undefined) setFieldValue('custom_comission_percentage', comission * 100)
  }

  const deleteService = async () => {
    try {
      setDeleting (true)
      
      await cutileiApi.delete (
        `/business_bills/${bill.id}/services/${service.id}`, requestConfig
      )

      setDeleting (false)
      handleDelete (service)
    } catch (error) {
      setDeleting (false)
      logApiError (error)
    }
  }

  const toggleEditting = () => setEditting (!editting)
  const toggleAlertDialog = () => setShowAlertDialog (!showAlertDialog)

  return editting ? (
    <Formik
      validationSchema={billServiceValidator}
      initialValues={{
        'service': service,
        'professional': service.professional ? {
          label: service.professional.nickname,
          value: service.professional.id
        } : null,
        'amount': service.amount,
        'custom_price': service.custom_price,
        'custom_comission_percentage': service.custom_comission_percentage * 100
      }}
      onSubmit={async (values, { setSubmitting }) => {
        const serviceData = {
          professional_id: values.professional?.value || null,
          amount: values.amount,
          custom_price: values.custom_price,
          custom_comission_percentage: values.custom_comission_percentage / 100
        }

        try {
          const { data: editedService } = await cutileiApi.put (
            `/business_bills/${bill.id}/services/${service.id}`, serviceData, requestConfig
          )

          setSubmitting (false)
          setEditting (false)
          handleEdit (editedService)
        } catch (error) {
          setSubmitting (false)
          logApiError (error)
        }
      }}
    >
      {({
        values,
        errors,
        touched,
        isSubmitting,
        setFieldValue,
        setFieldTouched,
        handleBlur, 
        handleSubmit
      }) => (
        <Container editting={editting} enableEditting={enableEditting}>
          <FormField>
            <Input
              name='service'
              placeholder='Serviço...'
              value={values.service.name}
              style={{width: '100%'}}
              disabled
            />
          </FormField>
          <FormField>
            <Select
              name='professional'
              placeholder='Profissional...'
              value={values.professional}
              options={professionals.map (professional => ({
                label: professional.nickname,
                value: professional.id
              }))}
              onChange={(name, option) => handleProfessionalSelected(name, option, setFieldValue)}
              onBlur={setFieldTouched}
              error={errors.professional}
              touched={touched.professional}
              containerStyles={{width: '100%'}}
            />
          </FormField>
          <FormField>
            <NumberInput
              id='amount'
              name='amount'
              value={values.amount}
              minValue={1}
              maxValue={99}
              disabled={!canEditAmount}
              onChange={setFieldValue}
              style={{width: '100%'}}
            />
          </FormField>
          <FormField>
            <MoneyInput
              name='custom_price'
              placeholder='Preço'
              value={values.custom_price}
              disabled={!canEditValue}
              onValueChange={setFieldValue}
              onBlur={handleBlur ('custom_price')}
              style={{width: '100%'}}
            />
          </FormField>
          <FormField>
            <NumberInput
              id='custom_comission_percentage'
              name='custom_comission_percentage'
              value={values.custom_comission_percentage}
              minValue={0}
              maxValue={100}
              disabled={!canEditComission}
              onChange={setFieldValue}
              style={{width: '100%'}}
            />
          </FormField>
          <Row>
            <Button type='button' onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting ? (
                <ButtonLoading style={{margin: 0}}/>
              ) : (
                <FA.FaSave color='#35A7FF' size={16}/>
              )}
            </Button>
            <Button type='button' onClick={toggleEditting}>
              <FA.FaTimes color='#FF3939' size={18}/>
            </Button>
          </Row>
        </Container>
      )}
    </Formik>
  ) : (
    <Container editting={editting} enableEditting={enableEditting}>
      <Row>
        {service.cutilei_service && <Image src={Logo} alt='Service Cutilei'/>}
        <InfoText>
          {service.nickname || service.name}
        </InfoText>
      </Row>
      <InfoText>
        {service.professional ? service.professional.nickname : '-'}
      </InfoText>
      <InfoText>
        {service.amount}
      </InfoText>
      <Row data-for={isFree ? service.id : null} data-tip>
        <MoneyTextMask
          value={(
            isFree ? showFreeItemsValue ? service.discount_price : 0 : service.custom_price
          ) ?? 0}
          style={{justifySelf: 'center'}}
        />
        {isFree && (
          <ReactTooltip
            id={service.id}
            effect='float'
            place='top'
            backgroundColor='#252525'
            tooltipRadius='10'
          >
            {freeService.earned_from === 'bundle' || (
              !service.bundle_id && !service.loyalty_card_id
            ) && (
              <TooltipLabel>
                O cliente possui este serviço em pacotes adquiridos, portanto ele não será cobrado.
              </TooltipLabel>
            )}
            {freeService.earned_from === 'loyalty_card' && (
              <TooltipLabel>
                O cliente ganhou este serviço em um cartão fidelidade, portanto ele não será cobrado.
              </TooltipLabel>
            )}
            {service.discount_price && (
              <TooltipLabel>
                (Valor do serviço no pacote: <MoneyTextMask value={service.discount_price} style={{fontSize: 14}}/>)
              </TooltipLabel>
            )}
          </ReactTooltip>
        )}
        {canBeFree && (
          <CheckRow>
            <CheckBox
              id={`is_free_${service.id}`}
              value={isFree}
              onValueChange={value => handleToggleFree (service, value)}
            />
            <Label htmlFor={`is_free_${service.id}`}>
              {freeService.earned_from === 'loyalty_card' ? 'Fidelidade' : 'Pacote'}
            </Label>
          </CheckRow>
        )}
      </Row>
      <InfoText>
        {service.custom_comission_percentage * 100}%
      </InfoText>
      {!service.cutilei_service && enableEditting && (
        <Row>
          {hasEditPermission && (
            <Button onClick={toggleEditting}>
              <FA.FaEdit color='#35A7FF' size={18}/>
            </Button>
          )}
          {hasDeletePermission && (
            <Button onClick={toggleAlertDialog}>
              {deleting ? (
                <ButtonLoading style={{margin: 0}}/>
              ) : (
                <FA.FaTrash color='#FF3939' size={16}/>
              )}
            </Button>
          )}
        </Row>
      )}
      <AlertDialog
        visible={showAlertDialog}
        title='Atenção!'
        message='Deseja realmente remover este serviço da comanda?'
        confirmText='Sim'
        cancelText='Não'
        onConfirm={deleteService}
        onClose={toggleAlertDialog}
        dangerous
      />
    </Container>
  )
}
